import React, { useState, useMemo, useEffect } from 'react';
import { styled } from 'styletron-react';
import { HeadingMedium } from 'baseui/typography';
import { Button, SIZE as BUTTON_SIZE } from 'baseui/button';
import { Input } from 'baseui/input';
import { Select } from 'baseui/select';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { Plus, Pencil, Search, X, Save } from 'lucide-react';
import { MantineDataTable } from '../../components/MantineDataTable';
import { MRT_ColumnDef } from 'mantine-react-table';
import { TableContent } from '../LayoutPage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultInstance } from '../../api/httpClient';
import { BilingualField } from '../../components/BilingualField';
import { useFdCan } from '../../hooks/useFdCan';

const TEMPLATE_CODES: { code: string; label: string; description: string; variables: string }[] = [
  {
    code: 'guest_checkin',
    label: 'Guest check-in',
    description: 'Sent to the host when their guest checks in at the gate.',
    variables: 'guest_name, estate_name',
  },
  {
    code: 'guest_checkout',
    label: 'Guest check-out',
    description: 'Sent to the host when their guest checks out at the gate.',
    variables: 'guest_name, estate_name',
  },
  {
    code: 'estate_approved',
    label: 'Estate approved',
    description: 'Sent to the requester when their estate access request is approved.',
    variables: 'estate_name, owner_name',
  },
  {
    code: 'estate_denied',
    label: 'Estate denied',
    description: 'Sent to the requester when their estate access request is denied.',
    variables: 'estate_name, owner_name',
  },
  {
    code: 'estate_pending',
    label: 'Estate pending',
    description: 'Sent to the owner when a new estate access request is awaiting their approval.',
    variables: 'estate_name, requester_name',
  },
];

// --- Styled ---

const Container = styled('div', { backgroundColor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' });
const Header = styled('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0 24px' });
const ToolbarRow = styled('div', { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px' });
const Main = styled('div', { flex: 1, overflow: 'auto', padding: '0 24px 24px' });

const SearchWrapper = styled('div', {
  display: 'flex', alignItems: 'center', gap: '8px', flex: '1', maxWidth: '280px',
  backgroundColor: '#EBEBEB', border: '1px solid #EBEBEB', borderRadius: '20px', padding: '7px 14px',
});
const SearchInputEl = styled('input', {
  border: 'none', outline: 'none', background: 'transparent', fontSize: '14px',
  color: '#374151', flex: 1, width: '100%', '::placeholder': { color: '#9CA3AF' },
});

const DrawerContent = styled('div', { padding: '24px', display: 'flex', flexDirection: 'column', height: '100%' });
const DrawerBody = styled('div', { flex: 1, overflow: 'auto' });
const DrawerFooter = styled('div', { paddingTop: '16px', borderTop: '1px solid #E5E7EB' });
const Label = styled('label', { fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' });
const FormGroup = styled('div', { marginBottom: '16px' });

const Hint = styled('div', {
  fontSize: '12px', color: '#6B7280', marginTop: '4px',
});

const VariableTag = styled('code', {
  display: 'inline-block', backgroundColor: '#EEF2FF', color: '#3730A3',
  padding: '2px 6px', borderRadius: '4px', fontSize: '12px', marginRight: '4px',
});

const EmptyState = styled('div', {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  padding: '60px 20px', color: '#6B7280', fontSize: '14px',
});

// --- Types ---

interface NotificationTemplate {
  id: number;
  code: string;
  title_en: string;
  title_es: string;
  body_en: string;
  body_es: string;
  description: string | null;
  variables: string | null;
  tenant_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const EMPTY_TEMPLATE: Partial<NotificationTemplate> = {
  code: '',
  title_en: '',
  title_es: '',
  body_en: '',
  body_es: '',
  description: '',
  variables: '',
  is_active: true,
};

// --- Hook ---

const useTemplates = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: async () => {
      // CRUD endpoint — `expand` is not needed; we want the raw row.
      const response = await defaultInstance({
        method: 'GET',
        url: '/notification_template?limit=500&order_by=code&order_direction=ASC',
      });
      return ((response as any)?.data?.data ?? (response as any)?.data ?? []) as NotificationTemplate[];
    },
  });
  return { templates: data || [], isLoading };
};

// --- Component ---

export const NotificationTemplates: React.FC = () => {
  const canEdit = useFdCan('notifications', 'edit');
  const queryClient = useQueryClient();
  const { templates, isLoading } = useTemplates();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editing, setEditing] = useState<Partial<NotificationTemplate>>(EMPTY_TEMPLATE);
  const [isCreate, setIsCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>('en');

  const reset = () => {
    setEditing(EMPTY_TEMPLATE);
    setIsCreate(false);
    setLanguage('en');
  };

  const openCreate = () => {
    reset();
    setIsCreate(true);
    setDrawerOpen(true);
  };

  const openEdit = (tpl: NotificationTemplate) => {
    setEditing(tpl);
    setIsCreate(false);
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!editing.code?.trim()) { toaster.negative('Code is required', { autoHideDuration: 2500 }); return; }
    if (!editing.title_en?.trim() || !editing.title_es?.trim()) { toaster.negative('Title (en & es) required', { autoHideDuration: 2500 }); return; }
    if (!editing.body_en?.trim() || !editing.body_es?.trim()) { toaster.negative('Body (en & es) required', { autoHideDuration: 2500 }); return; }

    setSaving(true);
    try {
      const payload = {
        code: editing.code?.trim(),
        title_en: editing.title_en?.trim(),
        title_es: editing.title_es?.trim(),
        body_en: editing.body_en?.trim(),
        body_es: editing.body_es?.trim(),
        description: editing.description ?? '',
        variables: editing.variables ?? '',
        is_active: editing.is_active ?? true,
      };

      if (isCreate) {
        await defaultInstance({ method: 'POST', url: '/notification_template', data: payload });
        toaster.positive('Template created', { autoHideDuration: 2500 });
      } else {
        await defaultInstance({
          method: 'PUT',
          url: `/notification_template/${editing.id}`,
          data: payload,
        });
        toaster.positive('Template updated', { autoHideDuration: 2500 });
      }
      setDrawerOpen(false);
      reset();
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
    } catch (err: any) {
      toaster.negative(err?.response?.data?.message || err?.message || 'Failed to save', { autoHideDuration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return templates;
    const q = searchQuery.toLowerCase();
    return templates.filter(t =>
      t.code.toLowerCase().includes(q) ||
      (t.title_en || '').toLowerCase().includes(q) ||
      (t.title_es || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [templates, searchQuery]);

  const columns = useMemo<MRT_ColumnDef<NotificationTemplate>[]>(() => [
    {
      accessorKey: 'code',
      header: 'CODE',
      size: 200,
      Cell: ({ row }) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: '13px' }}>{row.original.code}</div>
          {row.original.description && (
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.original.description}
            </div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'title_en',
      header: 'TITLE (EN / ES)',
      size: 320,
      Cell: ({ row }) => (
        <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
          <div>{row.original.title_en}</div>
          <div style={{ color: '#6B7280' }}>{row.original.title_es}</div>
        </div>
      ),
    },
    {
      accessorKey: 'tenant_id',
      header: 'SCOPE',
      size: 100,
      Cell: ({ cell }) => (
        <span style={{ fontSize: '12px', color: cell.getValue<string | null>() ? '#3730A3' : '#6B7280' }}>
          {cell.getValue<string | null>() ? 'Tenant' : 'Global'}
        </span>
      ),
    },
    {
      accessorKey: 'is_active',
      header: 'ACTIVE',
      size: 80,
      Cell: ({ cell }) => (
        <span style={{ fontSize: '12px' }}>{cell.getValue<boolean>() ? '✓' : '—'}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      // notifications is view + create for the tenant Front Desk: no edit.
      Cell: ({ row }) => (canEdit ? (
        <Button size="mini" kind="tertiary" onClick={() => openEdit(row.original)}>
          <Pencil size={14} />
        </Button>
      ) : null),
    },
  ], [canEdit]);

  // Reset edit state when the drawer closes so re-open is clean.
  useEffect(() => {
    if (!drawerOpen) reset();
  }, [drawerOpen]);

  return (
    <TableContent>
      <Container>
        <Header>
          <HeadingMedium margin={0}>Notification Templates</HeadingMedium>
          {/* SAM-1312 (U19): New Template only for who may edit templates */}
          {canEdit && (
          <Button
            onClick={openCreate}
            startEnhancer={() => <Plus size={16} />}
            size={BUTTON_SIZE.compact}
            overrides={{
              BaseButton: {
                style: { backgroundColor: 'var(--tenant-primary, #111827)', color: '#fff', borderRadius: '8px' },
              },
            }}
          >
            New Template
          </Button>
          )}
        </Header>

        <ToolbarRow>
          <SearchWrapper>
            <Search size={16} color="#9CA3AF" />
            <SearchInputEl placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchWrapper>
        </ToolbarRow>

        <Main>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
          ) : filtered.length === 0 ? (
            <EmptyState>
              <div>{searchQuery ? 'No matching templates' : 'No templates yet'}</div>
              <div style={{ fontSize: '12px', marginTop: 4 }}>Click "New Template" to add one</div>
            </EmptyState>
          ) : (
            <MantineDataTable
              columns={columns}
              data={filtered}
              enablePagination
              enableSorting
              initialPageSize={20}
              pageSizeOptions={[10, 20, 50]}
            />
          )}
        </Main>

        <Drawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          anchor={ANCHOR.right}
          size={SIZE.default}
          overrides={{
            Root: { style: { zIndex: 10000 } },
            DrawerBody: { style: { marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0, padding: 0 } },
            Close: { style: { display: 'none' } },
          }}
        >
          {/* Keyed by the template being edited: switching to a different row's
              "Edit" while the drawer is already open otherwise reuses the mounted
              BilingualField instances, letting a previous template's "dirty" flags
              leak into the newly selected one (see SAM-262). */}
          <DrawerContent key={isCreate ? 'create' : editing.id ?? 'create'}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{isCreate ? 'New Template' : `Edit: ${editing.code}`}</h3>
              <button onClick={() => setDrawerOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} color="#6B7280" />
              </button>
            </div>

            <DrawerBody>
              <FormGroup>
                <Label>Code *</Label>
                <Select
                  options={TEMPLATE_CODES.map(t => ({ id: t.code, label: t.label }))}
                  value={editing.code ? [{ id: editing.code, label: TEMPLATE_CODES.find(t => t.code === editing.code)?.label ?? editing.code }] : []}
                  onChange={({ value }) => {
                    const code = value[0]?.id as string | undefined;
                    if (!code) return;
                    const meta = TEMPLATE_CODES.find(t => t.code === code);
                    setEditing(prev => ({
                      ...prev,
                      code,
                      description: prev.description?.trim() ? prev.description : (meta?.description ?? ''),
                      variables: prev.variables?.trim() ? prev.variables : (meta?.variables ?? ''),
                    }));
                  }}
                  size="compact"
                  clearable={false}
                  searchable={false}
                  disabled={!isCreate}
                  overrides={{
                    Popover: { props: { overrides: { Body: { style: { zIndex: 100001 } } } } },
                    Dropdown: { style: { zIndex: 100001 } },
                  }}
                />
                <Hint>
                  {TEMPLATE_CODES.find(t => t.code === editing.code)?.description
                    || 'Pick a code wired to a backend Lambda. Arbitrary codes are ignored.'}
                </Hint>
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <Input
                  value={editing.description || ''}
                  onChange={e => setEditing(prev => ({ ...prev, description: e.currentTarget.value }))}
                  placeholder="What triggers this notification"
                  size="compact"
                />
              </FormGroup>

              <FormGroup>
                <Label>Variables (comma separated)</Label>
                <Input
                  value={editing.variables || ''}
                  onChange={e => setEditing(prev => ({ ...prev, variables: e.currentTarget.value }))}
                  placeholder="guest_name, estate_name"
                  size="compact"
                />
                <Hint>
                  Use them in title/body as <VariableTag>{'{guest_name}'}</VariableTag> etc.
                </Hint>
              </FormGroup>

              <FormGroup>
                {/* Language tab — typing in one language auto-translates the other (debounced 700ms). */}
                <div style={{ display: 'flex', gap: 4, padding: 4, background: '#F3F4F6', borderRadius: 8, marginBottom: 12 }}>
                  {(['en', 'es'] as const).map(lng => (
                    <button
                      key={lng}
                      type="button"
                      onClick={() => setLanguage(lng)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: 6,
                        background: language === lng ? '#fff' : 'transparent',
                        boxShadow: language === lng ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 13,
                        color: language === lng ? '#111827' : '#6B7280',
                      }}
                    >
                      {lng === 'en' ? 'English' : 'Español'}
                    </button>
                  ))}
                </div>

                <BilingualField
                  label="Title *"
                  language={language}
                  valueEn={editing.title_en || ''}
                  valueEs={editing.title_es || ''}
                  onChange={(lang, val) => setEditing(prev => ({ ...prev, [`title_${lang}`]: val }))}
                  placeholder={language === 'en' ? '{guest_name} checked in' : '{guest_name} hizo check-in'}
                />

                <BilingualField
                  label="Body *"
                  language={language}
                  valueEn={editing.body_en || ''}
                  valueEs={editing.body_es || ''}
                  onChange={(lang, val) => setEditing(prev => ({ ...prev, [`body_${lang}`]: val }))}
                  placeholder={language === 'en'
                    ? '{guest_name} just checked in. Get ready to receive them.'
                    : '{guest_name} acaba de hacer check-in. Prepárate para recibirlo.'}
                  multiline
                />
              </FormGroup>

              <FormGroup>
                <Checkbox
                  checked={editing.is_active ?? true}
                  onChange={e => setEditing(prev => ({ ...prev, is_active: e.currentTarget.checked }))}
                  labelPlacement={LABEL_PLACEMENT.right}
                >
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>Active</span>
                </Checkbox>
                <Hint>Inactive templates are skipped — the Lambda falls back to the literal text the caller sent.</Hint>
              </FormGroup>
            </DrawerBody>

            <DrawerFooter>
              <Button
                onClick={handleSave}
                disabled={saving}
                startEnhancer={() => <Save size={16} />}
                overrides={{
                  BaseButton: {
                    style: {
                      width: '100%', backgroundColor: 'var(--tenant-primary, #111827)', color: '#fff', borderRadius: '8px',
                      ':disabled': { backgroundColor: '#D1D5DB' },
                    },
                  },
                }}
              >
                {saving ? 'Saving…' : isCreate ? 'Create Template' : 'Save Changes'}
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Container>
    </TableContent>
  );
};
